<script lang="ts">
	import IconButton from '../../basic/buttons/IconButton.svelte';
	import { contextMenuState } from '../../basic/contextmenu/ContextMenuStore';
	import { confirmState } from '../../basic/dialog/ConfirmStore';
	import { openDocument } from '../../stores/DocumentViewStore.svelte';
	import type { SettingDocumentType } from '../../types/BasicTypes';
	import type { SelectableDocument } from '../../types/DocumentTypes';
	import type { Folder } from '../../types/FolderTypes';
	import { ViewMode } from '../../types/ListTypes';
	import DocumentTypeModal from '../settings/basic/settingdocumenttypes/DocumentTypeModal.svelte';
	import { _ } from 'svelte-i18n';
	import BaseStatusInfo from './BaseStatusInfo.svelte';
	import FolderMoveModal from './folder/FolderMoveModal.svelte';

	interface Props {
		viewMode: ViewMode;
		selectedItemCount: number;
		item: SelectableDocument;
		onDeleteDocument: (targetDocumentId: number) => Promise<void>;
		onMoveDocument: (targetFolderId: number, documentInformationId: number) => Promise<void>;
		onCopyDocument: (
			docType: SettingDocumentType,
			overrideWithDefaultValues: boolean
		) => Promise<void>;
		children?: import('svelte').Snippet;
		onClick: (e: MouseEvent) => void;
		onChildDeleted: () => void;
		onRequestContextmenu: (e: MouseEvent) => void;
	}

	let {
		viewMode,
		selectedItemCount,
		item = $bindable(),
		onDeleteDocument,
		onMoveDocument,
		onCopyDocument,
		onClick,
		children,
		onChildDeleted,
		onRequestContextmenu
	}: Props = $props();

	let showTypeSelection: boolean = $state(false);
	let showMenu: boolean = $state(false);
	let showMoveModal: boolean = $state(false);
	let overrideWithDefaultValues: boolean = $state(true);

	async function handleDeleteDocument() {
		await onDeleteDocument(item.id);
		onChildDeleted();
	}

	async function handleMoveDocument(targetFolder: Folder) {
		await onMoveDocument(targetFolder.id, item.documentInformation.id);

		//Hide the modal
		showMoveModal = false;
	}

	async function openContextMenu(e: MouseEvent) {
		if (selectedItemCount > 1 && item.selected) {
			onRequestContextmenu(e);
			return;
		}

		if (!item.selected) {
			onClick(e);
		}

		e.stopPropagation();
		const menuItems = [
			{
				title: $_('general.lbl_edit'),
				callbackFunction: () => {
					//Navigate to the detail page.
					openDocument(item);
				}
			},
			{
				title: $_('general.lbl_copy'),
				callbackFunction: () => {
					showTypeSelection = true;
				}
			},
			{
				title: $_('general.lbl_move'),
				callbackFunction: () => {
					showMoveModal = true;
				}
			},
			{
				title: $_('general.lbl_delete'),
				danger: true,
				disabled: item.finalizedDate != null || item.documentInformation.cancelDate != null,
				callbackFunction: () => {
					$confirmState = {
						bodyText: $_('document_list_view.lbl_document_delete_modal').replace('{}', item.name),
						successCallback: handleDeleteDocument,
						warningText: $_('document_list_view.lbl_document_delete_warning'),
						acceptButtonText: $_('general.lbl_delete')
					};
				}
			}
		];

		$contextMenuState = {
			x: e.clientX,
			y: e.clientY,
			menuItems: menuItems
		};
	}
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	oncontextmenu={(e: MouseEvent) => {
		e.preventDefault();
		openContextMenu(e);
	}}
	onmouseenter={() => {
		showMenu = true;
	}}
	onmouseleave={() => {
		showMenu = false;
	}}
>
	<!--Top container for more information on the state-->
	{#if viewMode != ViewMode.LIST}
		<div class="px-6">
			<BaseStatusInfo {item} {viewMode}></BaseStatusInfo>
		</div>
	{/if}

	<div class="relative">
		{@render children?.()}

		{#if showMenu && viewMode != ViewMode.LIST}
			<div class="absolute bottom-2 right-2">
				<IconButton
					iconName="ellipsis-h"
					onClick={openContextMenu}
					showCircle={false}
					size="small"
				/>
			</div>
		{/if}
	</div>
</div>

{#if showMoveModal}
	<FolderMoveModal
		onModalClose={() => {
			showMoveModal = false;
		}}
		onFolderSelected={(folder) => {
			handleMoveDocument(folder);
		}}
	/>
{/if}

{#if showTypeSelection}
	<DocumentTypeModal
		usedForCopy={true}
		bind:overrideWithDefaultValues
		onModalClose={() => (showTypeSelection = false)}
		header={$_('documentview.lbl_copy_document_header')}
		onSelectionChanged={(type) => {
			showTypeSelection = false;
			onCopyDocument(type, overrideWithDefaultValues);
			//Scroll to the top
			let listHTMLElement: HTMLElement | undefined = document.getElementsByName('list-container')?.[0];
			listHTMLElement?.scrollTo({
				top: 0,
				behavior: 'smooth'
			});
		}}
	/>
{/if}
